/**
 * VERSION-HISTORY (POS) — entity versions DERIVED from activity
 * inmutable `activity` (sin tabla dedicada).
 *
 * Why derived and not dedicated table: write-path already writes an entry
 * append-only for each change with patch `{field: {old, new}}`, actor, device
 * and timestamps, protected by triggers blocking UPDATE/DELETE (mig 00053).
 * Each entry IS a version: numbering and folding patches reconstructs
 * state without double-write or second source of truth that could
 * diverge. Multi-org isolation (CC-40) is inherited: the bucket
 * `activity_recent` only syncs user orgs and history
 * profundo pasa por `GET /api/activity/entity/:type/:id` (membership check).
 *
 * The "timestamp seal" of each version is a CHAINED 64-bit FNV-1a hash
 * (includes previous seal): deterministic and recomputable by any
 * client from immutable entries, so tampering with intermediate
 * version changes all subsequent seals. Not a persisted cryptographic
 * hash-chain (repo decision: immutability via triggers).
 */

import { type ActivityPatch, type ActivityRow, changedFields, parseActivityPatch } from "./format";

/** Known entity state at version (only fields seen in patches). */
export type EntitySnapshot = Record<string, unknown>;

export interface EntityVersion {
  /** 1-based ascending version number (v1 = oldest known entry). */
  version: number;
  /** Deterministic timestamp seal (16 hex, chained with previous version). */
  seal: string;
  /** Activity entry from which this version derives. */
  activityId: string;
  action: string | null;
  actorUserId: string | null;
  actorName: string | null;
  serverTs: string | null;
  clientTs: string | null;
  /** What changed in THIS version (field → {old, new}). */
  changes: ActivityPatch;
  /** Accumulated state after applying this version (folded patches). */
  snapshot: EntitySnapshot;
}

/** Milliseconds of textual timestamp; NaN-safe (NaNs sort at end). */
function tsValue(ts: string | null): number {
  if (!ts) {
    return Number.POSITIVE_INFINITY;
  }
  const parsed = Date.parse(ts);
  return Number.isNaN(parsed) ? Number.POSITIVE_INFINITY : parsed;
}

/**
 * Canonical ASCENDING chronological order: server_ts, tie-break by id
 * (UUIDv7 ⇒ id itself sorts by creation time).
 */
export function compareActivityAscending(
  a: Pick<ActivityRow, "server_ts" | "id">,
  b: Pick<ActivityRow, "server_ts" | "id">,
): number {
  const delta = tsValue(a.server_ts) - tsValue(b.server_ts);
  if (delta !== 0) {
    return delta;
  }
  return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
}

/** 64-bit FNV-1a over text code units; fixed 16-char hex. */
function fnv1a64(text: string): string {
  const prime = 0x1_00_00_00_01_b3n;
  const mask = 0xff_ff_ff_ff_ff_ff_ff_ffn;
  let hash = 0xcb_f2_9c_e4_84_22_23_25n;
  for (let i = 0; i < text.length; i += 1) {
    hash ^= BigInt(text.charCodeAt(i));
    hash = (hash * prime) & mask;
  }
  return hash.toString(16).padStart(16, "0");
}

/** Canonical stable form of patch (ordered fields, JSON values). */
export function canonicalPatch(patch: ActivityPatch): string {
  return changedFields(patch)
    .map((field) => {
      const change = patch[field];
      return `${field}:${JSON.stringify(change?.old ?? null)}>${JSON.stringify(change?.new ?? null)}`;
    })
    .join(";");
}

export interface VersionSealInput {
  entityType: string;
  entityId: string;
  version: number;
  activityId: string;
  serverTs: string | null;
  changes: ActivityPatch;
  /** Seal of previous version ("" for v1) — chains sequence. */
  prevSeal: string;
}

/**
 * Timestamp seal of version: deterministic hash of (entity, version number,
 * instante, entrada, cambios, sello anterior). Recomputable por cualquier
 * client from immutable activity — altering an intermediate version
 * invalidates all subsequent seals.
 */
export function computeVersionSeal(input: VersionSealInput): string {
  const canonical = [
    input.prevSeal,
    input.entityType,
    input.entityId,
    String(input.version),
    input.serverTs ?? "",
    input.activityId,
    canonicalPatch(input.changes),
  ].join("|");
  return fnv1a64(canonical);
}

/**
 * Builds version timeline of entity from its activity entries
 * activity (acepta cualquier orden; p. ej. el DESC de `getEntityActivityQuery`).
 * Returns ASCENDING versions v1..vN with accumulated snapshot and seal.
 * Never throws: malformed patches count as version without changes.
 */
export function buildEntityVersions(rows: readonly ActivityRow[]): EntityVersion[] {
  const ordered = rows.toSorted(compareActivityAscending);
  const versions: EntityVersion[] = [];
  let snapshot: EntitySnapshot = {};
  let prevSeal = "";
  for (const [index, row] of ordered.entries()) {
    const changes = parseActivityPatch(row.patch);
    snapshot = { ...snapshot };
    for (const field of changedFields(changes)) {
      snapshot[field] = changes[field]?.new;
    }
    const version = index + 1;
    const seal = computeVersionSeal({
      entityType: row.entity_type ?? "",
      entityId: row.entity_id ?? "",
      version,
      activityId: row.id,
      serverTs: row.server_ts,
      changes,
      prevSeal,
    });
    prevSeal = seal;
    versions.push({
      version,
      seal,
      activityId: row.id,
      action: row.action,
      actorUserId: row.actor_user_id,
      actorName: row.actor_name,
      serverTs: row.server_ts,
      clientTs: row.client_ts,
      changes,
      snapshot,
    });
  }
  return versions;
}

/**
 * Pure field-by-field diff between two reconstructed versions (patch style:
 * field → {old, new}). Compares by JSON structural equality.
 */
export function diffVersions(from: EntityVersion, to: EntityVersion): ActivityPatch {
  const fields = new Set([...Object.keys(from.snapshot), ...Object.keys(to.snapshot)]);
  const diff: ActivityPatch = {};
  for (const field of [...fields].toSorted()) {
    const oldValue = from.snapshot[field];
    const newValue = to.snapshot[field];
    if (JSON.stringify(oldValue ?? null) !== JSON.stringify(newValue ?? null)) {
      diff[field] = { old: oldValue, new: newValue };
    }
  }
  return diff;
}
